import { OverlayProvider } from "@toss/use-overlay";
import { ThemeProvider } from "@emotion/react";
import type { AppProps } from "next/app";
import { RecoilRoot } from "recoil";
import "@/styles/reset.css";
import "@/styles/theme/font.css";
import "goi_common/src/design-system/tokens/typography.css";

import { ChakraProvider } from "@chakra-ui/react";
import { getUserId, HackleProvider } from "@hackler/react-sdk";

import AOS from "aos";
import "aos/dist/aos.css";
import {
  chakraTheme,
  getUserProperties,
  initUserProperties,
  theme,
  HackleClient,
  GoiDevTools,
} from "goi_common";

import Script from "next/script";
import { ReactElement, ReactNode, useEffect, useState } from "react";
import { NextPage } from "next";
import { Hydrate, QueryClient, QueryClientProvider } from "react-query";

import "slick-carousel/slick/slick.css";
import Layout from "@/components/Layout";
import Head from "next/head";
import { naverPageView } from "@/utils/naverPageView";
import { mobionPageView, mobionDataManagerPageView } from "@/utils/mobionPixel";
import { Router, useRouter } from "next/router";
import { GlobalStyle } from "@/styles/GlobalStyle";
import { ReactQueryDevtools } from "react-query/devtools";
import { initIdentity } from "@/utils/marketingId";
import { GoiLastTouchScript } from "@/components/GoiLastTouchScript";
import { useDisablePinchZoom } from "@/hooks/useDisablePinchZoom";

declare global {
  interface Window {
    kakao: any;
    naver: any;
    hackleClinet: any;
    eziok_std_process: (url: string, type: string, callback: string) => void;
    TossCert: any;
    CallMtm: ((...args: unknown[]) => void) & { q?: unknown[][] };
    callDM: ((...args: unknown[]) => void) & { q?: unknown[][] };
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  },
});

// eslint-disable-next-line @typescript-eslint/ban-types
export type NextPageWithLayout<P = {}, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function MyApp({ Component, pageProps }: AppPropsWithLayout) {
  const router = useRouter();

  // 특정 프로모션 랜딩 페이지에서 핀치 줌 방지
  const disablePinchZoom = router.asPath.includes("/promotion/event/26-gas");
  useDisablePinchZoom(disablePinchZoom);

  const dableScript = `
      (function(d,a,b,l,e,_) {
      d[b]=d[b]||function(){(d[b].q=d[b].q||[]).push(arguments)};e=a.createElement(l);
      e.async=1;e.charset='utf-8';e.src='//static.dable.io/dist/dablena.min.js';
      _=a.getElementsByTagName(l)[0];_.parentNode.insertBefore(e,_);
      })(window,document,'dablena','script');
      dablena('init', '001-005-680');
      dablena('track', 'PageView');
    `;

  const googleTagManagerScript = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_GTM}');
    `;

  const facebookPixelScript = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '1159415875062121');
    fbq('track', 'PageView');
  `;

  const daangnScript = `
    var script = document.createElement('script');
    script.src = 'https://karrot-pixel.business.daangn.com/0.4/karrot-pixel.umd.js';
    script.onload = function() {
      window.karrotPixel.init('1731308335525700001');
    };
    document.head.appendChild(script);
  `;

  const tiktokPixelScript = `
    !function (w, d, t) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(
      var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=r,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script")
      ;n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};
      ttq.load('D8LTCSJC77U12CTGTGEG');
      ttq.page();
    }(window, document, 'ttq');
  `;

  const targetingGatesScript = `
    var wptg_tagscript_vars = wptg_tagscript_vars || [];
    wptg_tagscript_vars.push(function() {
      return {
        wp_hcuid: "",
        ti: "55912",
        ty: "Home",
        device: "web"
      };
    });
  `;

  const handleResize = () => {
    const vh = window.innerHeight * 0.01;

    document.documentElement.style.setProperty("--vh", `${vh}px`);
  };

  useEffect(() => {
    AOS.init();
    initIdentity();
    const goiMemberId = localStorage.getItem("goi-member-id");

    if (goiMemberId) setMemberId(goiMemberId);
  }, []);

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [memberId, setMemberId] = useState("");

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      naverPageView();
      mobionPageView();
      mobionDataManagerPageView();
    };
    Router.events.on("routeChangeComplete", handleRouteChange);

    return () => Router.events.off("routeChangeComplete", handleRouteChange);
  }, []);

  useEffect(() => {
    if (HackleClient) {
      initUserProperties();
      let id = getUserId();

      if (memberId) {
        id = memberId;
      }

      const user = {
        userId: id,
        properties: getUserProperties(),
      };

      HackleClient.setUser(user);
    }
  }, [memberId]);

  const Components = (
    <HackleProvider hackleClient={HackleClient} supportSSR>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <ChakraProvider resetCSS={true} theme={chakraTheme}>
          <QueryClientProvider client={queryClient}>
            <Hydrate state={pageProps.dehydratedState}>
              <RecoilRoot>
                <OverlayProvider>
                  <Layout>
                    <Component {...pageProps} />
                    <GoiDevTools />
                  </Layout>
                </OverlayProvider>
              </RecoilRoot>
              <ReactQueryDevtools />
            </Hydrate>
          </QueryClientProvider>
        </ChakraProvider>
      </ThemeProvider>
    </HackleProvider>
  );

  return (
    <>
      <Head>
        <title>
          {pageProps.pageOgTitle
            ? pageProps.pageOgTitle
            : "똑똑한 소비자를 위한 100원 상조 | 고이 프라임"}
        </title>
        <meta
          name="viewport"
          content={
            disablePinchZoom
              ? "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
              : "width=device-width, initial-scale=1.0"
          }
        />
      </Head>
      <header>
        {process.env.NEXT_PUBLIC_IS_PRODUCTION === "true" && (
          <>
            <Script
              id="gtm"
              strategy="afterInteractive"
              dangerouslySetInnerHTML={{ __html: googleTagManagerScript }}
            />
            <Script
              src="//wcs.naver.net/wcslog.js"
              onLoad={() => naverPageView()}
            />
            <Script
              id="fb-pixel"
              dangerouslySetInnerHTML={{ __html: facebookPixelScript }}
            />
            <Script
              type="text/javascript"
              charSet="UTF-8"
              src="//t1.daumcdn.net/kas/static/kp.js"
            />
            <Script
              id="dable"
              dangerouslySetInnerHTML={{ __html: dableScript }}
            />
            <Script
              id="daangn"
              dangerouslySetInnerHTML={{
                __html: daangnScript,
              }}
            />
            <Script
              id="tiktok-pixel"
              dangerouslySetInnerHTML={{
                __html: tiktokPixelScript,
              }}
            />
            <Script
              id="mobion-onetag"
              strategy="afterInteractive"
              src={`https://cdn.onetag.co.kr/0/tcs.js?eid=${process.env.NEXT_PUBLIC_MOBION_EID}`}
            />
            <Script
              id="toss-pixel"
              type="text/javascript"
              strategy="afterInteractive"
              charSet="UTF-8"
              src="https://static.toss.im/lex/v1.js"
              onLoad={() => {
                // Toss Pixel 로드 완료 시점을 전역으로 알림
                window.tossPixelLoaded = true;
                window.dispatchEvent(new CustomEvent("tossPixelLoaded"));
              }}
            />
          </>
        )}
        <Script
          id="eziok"
          src={`https://${process.env.NEXT_PUBLIC_EZI_CERT_URL}/stdauth/ds_auth_ptb/asset/js/ptb_ezauth_proc.js`}
          dangerouslySetInnerHTML={{
            __html: "window.eziok_std_process = eziok_std_process",
          }}
        />
        <Script id="toss-cert" src="https://cdn.toss.im/cert/v1" />
      </header>
      {Components}

      {process.env.NEXT_PUBLIC_IS_PRODUCTION === "true" && (
        <>
          <div id="wp_tg_cts" style={{ display: "none" }} />
          <Script
            id="targeting-gates-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{ __html: targetingGatesScript }}
          />
          <Script
            id="targeting-gates-script"
            strategy="afterInteractive"
            src="//cdn-aitg.widerplanet.com/js/wp_astg_4.0.js"
          />
        </>
      )}
      <Script
        src="https://ezauth.settlebank.co.kr/js/SettlePay.js"
        strategy="lazyOnload"
      />
      <GoiLastTouchScript />
    </>
  );
}

export default MyApp;
